import React, { useState } from 'react';
import { Button, Card, Input, Modal, Space, Table, Upload, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import { getErrorKeys } from 'utils/exceptionUtils';
import { fileUpload } from 'utils/storageUtils';

/** 初始化导入结果（两个导入共用） */
export interface DataImportResult {
    totalRows: number;
    successCount: number;
    skippedCount: number;
    failures: DataImportFailure[];
}

export interface DataImportFailure {
    rowNumber: number;
    rowContent: string;
    reason: string;
}

const IMPORT_RESULT_FIELDS = `
    totalRows
    successCount
    skippedCount
    failures {
        rowNumber
        rowContent
        reason
    }
`;

/** 客户企微群与等级导入由 api-server 处理 */
const IMPORT_CHANNEL_WECOM_GROUPS = gql`
    mutation OpsToolImportChannelWecomGroups($input: DataImportInput!) {
        opsToolImportChannelWecomGroups(input: $input) { ${IMPORT_RESULT_FIELDS} }
    }
`;

/** 基础数据通用操作：透传 base-data，结果以 JSON 字符串返回；站点账号标签导入走这里 */
const EXECUTE_DATA_OPS = gql`
    mutation OpsToolExecuteDataOps($input: OpsDataOpsInput!) {
        opsToolExecuteDataOps(input: $input) {
            type
            success
            messageKey
            dataJson
        }
    }
`;

/** 与后端 ChannelMutation 导入、base-data 站点账号标签导入的上限一致 */
const MAX_FILE_MB = 2;

/** 站点账号标签导入的 type 与 base-data BaseDataOpsType 枚举名一致 */
type ImportType = 'IMPORT_CHANNEL_WECOM_GROUP' | 'IMPORT_SITE_ACCOUNT_TAG';

const IMPORT_OPTIONS: { type: ImportType; label: string; hint: string }[] = [
    {
        type: 'IMPORT_CHANNEL_WECOM_GROUP',
        label: '导入客户企微群与等级',
        hint: '企微群ID | 客户ID | 企微群名称 | 客户等级（SKA / KA / MA / LA / SMB），同一客户可有多行；群名称以企微接口查询为准',
    },
    { type: 'IMPORT_SITE_ACCOUNT_TAG', label: '导入站点账号标签', hint: 'lark账号 | 手机号 | 系统账号 | 角色 | hub（站点名称） | 备注，角色和 hub 可用「/」写多个' },
];

/** 运维工具不做国际化：后端返回的 messageKey 在页面直接对应中文 */
const ERROR_TEXTS: Record<string, string> = {
    'errors.validation.dataImport.fileInvalid': '文件无法解析，请上传 Excel（.xlsx）文件',
    'errors.validation.dataImport.fileTooLarge': `文件不能超过 ${MAX_FILE_MB}MB`,
    'errors.validation.dataImport.rowsTooMany': '数据行数不能超过 5000 行',
    'errors.validation.siteAccountTag.operatorRequired': '操作人不能为空',
    'errors.validation.siteAccountTag.importFileRequired': '导入文件不能为空',
    'errors.validation.siteAccountTag.importFileInvalid': '文件不存在或无法解析，请上传 Excel（.xlsx）文件',
    'errors.validation.siteAccountTag.importFileTooLarge': '文件不能超过 2MB',
    'errors.validation.siteAccountTag.importRowsTooMany': '数据行数不能超过 5000 行',
    'errors.system.lark.rateLimited': 'Lark 接口调用过于频繁，请稍后重试',
    'errors.validation.ops.typeInvalid': '不支持的运维操作类型',
    'errors.validation.ops.paramsInvalid': '操作参数为空或格式错误',
    'errors.validation.ops.operatorRequired': '操作人不能为空',
    'errors.validation.channel.idRequired': '客户ID不能为空',
    'errors.validation.channel.operatorRequired': '操作人不能为空',
    'errors.validation.channel.customerLevelInvalid': '客户等级不能超过 16 个字符',
    'errors.business.channel.notFound': '客户不存在或已删除',
    'errors.business.channel.baseNotFound': '客户基础信息不存在',
    'errors.system.serviceUnavailable': '依赖服务暂不可用，请稍后重试',
};

const errorText = (messageKey?: string | null): string =>
    (messageKey && ERROR_TEXTS[messageKey]) || `操作失败：${messageKey || '系统异常'}`;

/** 调用异常（GraphQL 报错）时按 messageKey 提示中文 */
const showOpsError = (error: unknown) => message.error(errorText(getErrorKeys(error).messageKey));

/** 透传 base-data 的通用操作：失败时按返回的 messageKey 抛出，由调用处统一提示 */
class OpsFailure extends Error {
    constructor(readonly messageKey?: string | null) {
        super(messageKey ?? 'ops failed');
    }
}

const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            const comma = typeof dataUrl === 'string' ? dataUrl.indexOf(',') : -1;
            if (comma === -1) {
                reject(new Error('Failed to read file'));
                return;
            }
            resolve((dataUrl as string).slice(comma + 1));
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });

const columns: TableColumnsType<DataImportFailure> = [
    { title: '行号', dataIndex: 'rowNumber', key: 'rowNumber', width: 80 },
    { title: '当前行内容', dataIndex: 'rowContent', key: 'rowContent' },
    { title: '失败原因', dataIndex: 'reason', key: 'reason' },
];

/**
 * 运维工具「基础数据 → 初始化导入」：客户企微群与等级、站点账号标签两个导入按钮，结果展示在下方。
 * 合格的行写入，失败行展示行号、内容和原因；已存在的计为跳过，修正后可重复导入。
 * 另提供单个客户的等级修改（透传 base-data 的 UPDATE_CUSTOMER_LEVEL），确认后执行
 */
const InitDataImportPage: React.FC = () => {
    const [importChannelWecomGroups] = useMutation(IMPORT_CHANNEL_WECOM_GROUPS);
    const [executeDataOps] = useMutation(EXECUTE_DATA_OPS);
    const [importing, setImporting] = useState<ImportType | null>(null);
    const [result, setResult] = useState<{ label: string; fileName: string; data: DataImportResult } | null>(null);
    const [levelChannelId, setLevelChannelId] = useState('');
    const [customerLevel, setCustomerLevel] = useState('');
    const [updatingLevel, setUpdatingLevel] = useState(false);

    const updateCustomerLevel = async (channelId: number, level: string) => {
        setUpdatingLevel(true);
        try {
            // 等级为空表示清空
            const paramsJson = JSON.stringify({ channelId, customerLevel: level || null });
            const response = await executeDataOps({ variables: { input: { type: 'UPDATE_CUSTOMER_LEVEL', paramsJson } } });
            const payload = response.data?.opsToolExecuteDataOps;
            if (payload?.success) {
                message.success('修改成功');
            } else {
                message.error(errorText(payload?.messageKey));
            }
        } catch (error) {
            showOpsError(error);
        } finally {
            setUpdatingLevel(false);
        }
    };

    const handleUpdateLevelClick = () => {
        const text = levelChannelId.trim();
        if (!/^\d+$/.test(text) || Number(text) <= 0) {
            message.error('请输入正确的客户ID');
            return;
        }
        const channelId = Number(text);
        // 等级不校验取值，按输入保存（只去首尾空格），新增或调整等级不必改代码
        const level = customerLevel.trim();
        Modal.confirm({
            title: '确认修改客户等级',
            content: level ? `将客户 ${channelId} 的等级修改为 ${level}` : `将清空客户 ${channelId} 的等级`,
            okText: '确定',
            cancelText: '取消',
            onOk: () => updateCustomerLevel(channelId, level),
        });
    };

    const runImport = async (type: ImportType, file: File): Promise<DataImportResult | undefined> => {
        if (type === 'IMPORT_SITE_ACCOUNT_TAG') {
            // 文件先直传 R2，再把对象键交给 base-data
            const { objectKey } = await fileUpload(file, 'site-account-tag-import');
            const paramsJson = JSON.stringify({ s3Key: objectKey, fileName: file.name });
            const response = await executeDataOps({ variables: { input: { type, paramsJson } } });
            const payload = response.data?.opsToolExecuteDataOps;
            if (!payload?.success) {
                throw new OpsFailure(payload?.messageKey);
            }
            return payload.dataJson ? (JSON.parse(payload.dataJson) as DataImportResult) : undefined;
        }
        const input = { fileBase64: await readFileAsBase64(file), fileName: file.name };
        const response = await importChannelWecomGroups({ variables: { input } });
        return response.data?.opsToolImportChannelWecomGroups ?? undefined;
    };

    const handleUpload = async (type: ImportType, label: string, file: File) => {
        if (!/\.xlsx?$/i.test(file.name)) {
            message.error('只支持 Excel 文件（.xlsx / .xls）');
            return;
        }
        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            message.error(`文件不能超过 ${MAX_FILE_MB}MB`);
            return;
        }
        setImporting(type);
        try {
            const data = await runImport(type, file);
            if (data) {
                setResult({ label, fileName: file.name, data });
                message.success(`${label}完成`);
            }
        } catch (error) {
            if (error instanceof OpsFailure) {
                message.error(errorText(error.messageKey));
            } else {
                showOpsError(error);
            }
        } finally {
            setImporting(null);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <Card title="初始化导入">
                <div className="flex flex-col gap-3">
                    {IMPORT_OPTIONS.map(({ type, label, hint }) => (
                        <Space key={type}>
                            <Upload
                                accept=".xlsx,.xls"
                                showUploadList={false}
                                disabled={importing !== null}
                                beforeUpload={(file) => {
                                    handleUpload(type, label, file);
                                    return false;
                                }}
                            >
                                <Button icon={<UploadOutlined />} loading={importing === type} disabled={importing !== null && importing !== type}>
                                    {label}
                                </Button>
                            </Upload>
                            <span className="text-gray-500">{hint}</span>
                        </Space>
                    ))}
                    <span className="text-gray-400">第 1 行为表头；文件不超过 {MAX_FILE_MB}MB；已存在的数据计为跳过，修正失败行后可重复导入</span>
                </div>
            </Card>
            <Card title="修改客户等级">
                <Space wrap>
                    <Input
                        placeholder="客户ID"
                        value={levelChannelId}
                        onChange={(e) => setLevelChannelId(e.target.value)}
                        className="w-48"
                        allowClear
                    />
                    <Input
                        placeholder="客户等级（不填表示清空）"
                        value={customerLevel}
                        onChange={(e) => setCustomerLevel(e.target.value)}
                        className="w-80"
                        allowClear
                    />
                    <Button type="primary" loading={updatingLevel} onClick={handleUpdateLevelClick}>
                        确定
                    </Button>
                </Space>
            </Card>
            {result && (
                <Card title={`${result.label}结果：${result.fileName}`}>
                    <Space size="large" className="mb-4">
                        <span>总行数：{result.data.totalRows}</span>
                        <span className="text-green-600">成功：{result.data.successCount}</span>
                        <span>跳过：{result.data.skippedCount}</span>
                        <span className="text-red-500">失败：{result.data.failures.length}</span>
                    </Space>
                    <Table<DataImportFailure>
                        rowKey="rowNumber"
                        size="small"
                        columns={columns}
                        dataSource={result.data.failures}
                        pagination={{ pageSize: 50, showSizeChanger: false }}
                    />
                </Card>
            )}
        </div>
    );
};

export default InitDataImportPage;
